import React, { useEffect, useState, useMemo, useRef, memo } from "react";
import {
  Paper,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { getAuthToken } from "../../utils/getAuthToken";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

const formatDate = (date) => dayjs(date).format('YYYY-MM-DD');

const getDateArray = (start, end) => {
  const arr = [];
  let dt = dayjs(start);
  while (dt.isBefore(end) || dt.isSame(end, 'day')) {
    arr.push(dt.format('YYYY-MM-DD'));
    dt = dt.add(1, 'day');
  }
  return arr;
};

/** Isolated from parent re-renders (e.g. app-wide 1s connection tick) so Recharts axes do not redraw every second. */
const WorkflowPerformanceLineChart = memo(function WorkflowPerformanceLineChart({
  data,
  syncId,
  height,
  tickFontSize,
  axisTickColor,
  tooltipContentStyle,
  tooltipLabelStyle,
  legendFontSize,
  lineStrokeWidth,
  compactChart,
}) {
  const chartMargin = compactChart
    ? { top: 12, right: 4, left: 4, bottom: 6 }
    : { top: 20, right: 30, left: 0, bottom: 0 };

  return (
    <Box
      sx={
        compactChart
          ? {
              width: "100%",
              maxWidth: "100%",
              display: "flex",
              justifyContent: "flex-start",
              ml: { xs: -1, sm: -1 },
              "& .recharts-responsive-container": {
                width: "100% !important",
                maxWidth: "100%",
              },
            }
          : undefined
      }
    >
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={chartMargin} syncId={syncId}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="date"
          interval="preserveStartEnd"
          tick={{ fontSize: tickFontSize, fill: axisTickColor }}
          allowDataOverflow={false}
        />
        <YAxis
          allowDecimals={false}
          width={compactChart ? 28 : undefined}
          tick={{ fontSize: tickFontSize, fill: axisTickColor }}
        />
        <Tooltip
          contentStyle={tooltipContentStyle}
          labelStyle={tooltipLabelStyle}
          isAnimationActive={false}
        />
        <Legend
          align="center"
          verticalAlign="bottom"
          layout="horizontal"
          wrapperStyle={{
            fontSize: legendFontSize,
            width: "100%",
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: "8px 16px",
            paddingTop: 8,
          }}
        />
        <Line
          type="monotone"
          dataKey="Enrolled"
          stroke="#6c3483"
          name="Enrolled"
          dot={false}
          strokeWidth={lineStrokeWidth}
          isAnimationActive={false}
        />
        <Line
          type="monotone"
          dataKey="Completed"
          stroke="#229954"
          name="Completed"
          dot={false}
          strokeWidth={lineStrokeWidth}
          isAnimationActive={false}
        />
        <Line
          type="monotone"
          dataKey="Abandoned"
          stroke="#e53935"
          name="Abandoned"
          dot={false}
          strokeWidth={lineStrokeWidth}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
    </Box>
  );
});

const AnalyticsTab = ({ workflowId }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isLg = useMediaQuery(theme.breakpoints.between('lg', 'xl'));
  const today = useMemo(() => dayjs(), []);
  const defaultStart = useMemo(() => dayjs().subtract(16, 'day'), []);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    enrolled: { dates: [], count: 0 },
    completed: { dates: [], count: 0 },
    abandoned: { dates: [], count: 0 },
  });

  // Get stable string representations of dates for dependency tracking
  const startDateStr = startDate ? startDate.format('YYYY-MM-DD') : '';
  const endDateStr = endDate ? endDate.format('YYYY-MM-DD') : '';

  // Memoize date labels - only recalculate when date strings change
  const dateLabels = useMemo(() => {
    if (!startDate || !endDate) return [];
    return getDateArray(startDate, endDate);
  }, [startDate, endDate]);

  // Create stable date format array - pre-compute all formatted dates
  // This ensures the same date always gets the same formatted string
  const formattedDates = useMemo(() => {
    if (!dateLabels || dateLabels.length === 0) return [];
    return dateLabels.map(date => dayjs(date).format('MMM DD'));
  }, [dateLabels]); // Only recalculate when date range changes

  // Use refs to track previous data and prevent unnecessary recalculations
  const prevStatsRef = useRef(null);
  const prevChartDataRef = useRef([]);
  const prevDateRangeRef = useRef({ start: '', end: '' });

  // Memoize chart data with stable date formatting
  // Only recalculate when date range or stats actually change
  const chartData = useMemo(() => {
    if (!dateLabels || dateLabels.length === 0 || !formattedDates || formattedDates.length === 0) {
      prevChartDataRef.current = [];
      return [];
    }
    if (dateLabels.length !== formattedDates.length) {
      return prevChartDataRef.current;
    }
    
    // Check if date range changed
    const dateRangeChanged = 
      prevDateRangeRef.current.start !== startDateStr || 
      prevDateRangeRef.current.end !== endDateStr;
    
    // Create a stable hash of stats to compare content, not references
    const statsHash = JSON.stringify({
      enrolled: stats.enrolled.dates.map(d => formatDate(d)).sort(),
      completed: stats.completed.dates.map(d => formatDate(d)).sort(),
      abandoned: stats.abandoned.dates.map(d => formatDate(d)).sort(),
    });
    
    // Check if stats actually changed
    const statsChanged = prevStatsRef.current !== statsHash;
    
    // If neither date range nor stats changed, return previous data
    if (!dateRangeChanged && !statsChanged && prevChartDataRef.current.length > 0) {
      return prevChartDataRef.current;
    }
    
    // Update refs
    prevDateRangeRef.current = { start: startDateStr, end: endDateStr };
    prevStatsRef.current = statsHash;
    
    // Use pre-computed formatted dates to ensure stability
    const newChartData = dateLabels.map((date, index) => ({
      date: formattedDates[index] || dayjs(date).format('MMM DD'),
      Enrolled: stats.enrolled.dates.filter(d => formatDate(d) === date).length,
      Completed: stats.completed.dates.filter(d => formatDate(d) === date).length,
      Abandoned: stats.abandoned.dates.filter(d => formatDate(d) === date).length,
    }));
    
    prevChartDataRef.current = newChartData;
    return newChartData;
  }, [dateLabels, formattedDates, startDateStr, endDateStr,
      stats.enrolled.dates, stats.completed.dates, stats.abandoned.dates]);

  useEffect(() => {
    if (!workflowId) return;
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      
      // Get token from memory (not localStorage)
      const token = await getAuthToken();
      
      if (!token) {
        setError("Authentication required");
        setLoading(false);
        return;
      }

      try {
        const res = await axios.post(
          `${SERVER_DOMAIN}/api/workflow/getanalytics/${workflowId}`,
          {
            startTime: startDate.startOf('day').toISOString(),
            endTime: endDate.endOf('day').toISOString(),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setStats({
          enrolled: res.data.enrolled || { dates: [], count: 0 },
          completed: res.data.completed || { dates: [], count: 0 },
          abandoned: res.data.abandoned || { dates: [], count: 0 },
        });
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch analytics");
        setStats({ enrolled: { dates: [], count: 0 }, completed: { dates: [], count: 0 }, abandoned: { dates: [], count: 0 } });
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [workflowId, startDate, endDate]);

  const chartHeight = isXs ? 180 : isSm ? 200 : isMd ? 210 : isLg ? 215 : 220;
  const tickFontSize = isXs ? 10 : isSm ? 11 : isMd ? 11.5 : isLg ? 12 : 12;
  const axisTickColor = theme.palette.text.secondary;
  const legendFontSize = isXs ? '0.7rem' : isSm ? '0.75rem' : isMd ? '0.8rem' : isLg ? '0.85rem' : '0.9rem';
  const tooltipFontSize = isXs ? '0.7rem' : isSm ? '0.75rem' : isMd ? '0.8rem' : isLg ? '0.85rem' : '0.9rem';
  const lineStrokeWidth = isXs || isSm ? 1.5 : 2;

  const tooltipStyles = useMemo(
    () => ({
      contentStyle: {
        fontSize: tooltipFontSize,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
      },
      labelStyle: {
        color: theme.palette.text.primary,
        fontWeight: 600,
      },
    }),
    [
      tooltipFontSize,
      theme.palette.background.paper,
      theme.palette.divider,
      theme.palette.text.primary,
    ]
  );

  return (
    <Box sx={{ 
      mx: "auto", 
      p: { xs: 0.75, sm: 1, md: 2, lg: 2.5, xl: 3 }, 
      backgroundColor: (theme) => theme.palette.background.default, 
      height: '100%',
      flex: { xs: 1, sm: 1 },
      minHeight: { xs: 0, sm: 0 },
      overflow: 'auto',
      // xs/sm: scroll works, scrollbar hidden
      scrollbarWidth: { xs: 'none', sm: 'none', md: 'thin', lg: 'thin', xl: 'thin' },
      msOverflowStyle: { xs: 'none', sm: 'none', md: 'auto', lg: 'auto', xl: 'auto' },
      '&::-webkit-scrollbar': {
        width: { xs: 0, sm: 0, md: '8px', lg: '10px', xl: '10px' },
      },
      '&::-webkit-scrollbar-track': {
        background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
      },
      '&::-webkit-scrollbar-thumb': {
        background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
        borderRadius: '10px',
      },
    }}>
      <Grid container spacing={{ xs: 2, sm: 3, md: 4, lg: 5, xl: 5 }}>
        {/* Left: (Optional) Preview or Info Card - can add here if needed */}
        <Grid item xs={12} md={12}>
          {/* Date Range Picker Row */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              alignItems: { xs: 'stretch', sm: 'center' },
              mb: { xs: 1, sm: 1.25, md: 2, lg: 2, xl: 2 },
              flexWrap: 'wrap',
              gap: { xs: 0.75, sm: 1, md: 1.5, lg: 2, xl: 2 },
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: (theme) => theme.palette.mode === 'dark' ? '#fff' : 'inherit',
                  mr: { xs: 0, sm: 1 },
                  fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem', lg: '0.95rem', xl: '1rem' },
                  alignSelf: { xs: 'flex-start', sm: 'center' }
                }}
              >
                Date Range:
              </Typography>
              <DatePicker
                label="Start date"
                value={startDate}
                maxDate={endDate}
                onChange={newValue => newValue && setStartDate(newValue)}
                slotProps={{ 
                  textField: { 
                    size: isXs || isSm ? 'small' : 'medium',
                    sx: {
                      '& .MuiInputBase-root': {
                        fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem', lg: '0.95rem', xl: '1rem' }
                      }
                    }
                  } 
                }}
              />
              <DatePicker
                label="End date"
                value={endDate}
                minDate={startDate}
                maxDate={today}
                onChange={newValue => newValue && setEndDate(newValue)}
                slotProps={{ 
                  textField: { 
                    size: isXs || isSm ? 'small' : 'medium',
                    sx: {
                      '& .MuiInputBase-root': {
                        fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem', lg: '0.95rem', xl: '1rem' }
                      }
                    }
                  } 
                }}
              />
            </LocalizationProvider>
          </Box>
          {/* Analytics Content with scroll if overflow */}
          <Box sx={{ 
            pr: { xs: 0, sm: 0.5, md: 0.75, lg: 1, xl: 1 },
            pb: { xs: 2, sm: 2, md: 0, lg: 0, xl: 0 }
          }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: { xs: 150, sm: 180, md: 200, lg: 200, xl: 200 } }}>
                <CircularProgress size={isXs || isSm ? 30 : 40} />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem', lg: '0.9rem', xl: '1rem' } }}>
                {error}
              </Alert>
            ) : (
              <>
                {/* Summary boxes for Enrolled, Completed, Abandoned */}
                <Grid container spacing={{ xs: 2, sm: 3, md: 4, lg: 5, xl: 5 }} sx={{ mb: 0 }} justifyContent="center">
                  <Grid item xs={12} sm={4} md={3} sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'center' } }}>
                    <Paper sx={{ 
                      p: { xs: 1, sm: 1.5, md: 1.75, lg: 2, xl: 2 }, 
                      textAlign: "center", 
                      borderRadius: { xs: 1.5, sm: 1.75, md: 1.75, lg: 2, xl: 2 }, 
                      backgroundColor: (theme) => theme.palette.background.paper,
                      width: {
                        xs: '100%',
                        sm: 'min(100%, 280px)',
                        md: 'min(100%, 320px)',
                        lg: 'min(100%, 340px)',
                        xl: 'min(100%, 360px)',
                      },
                    }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '0.875rem' } }}
                      >
                        Enrolled
                      </Typography>
                      <Typography 
                        variant={isXs || isSm ? 'body1' : 'h6'}
                        sx={{ 
                          mt: { xs: 0.5, sm: 0.75, md: 0.875, lg: 1, xl: 1 }, 
                          fontWeight: 700, 
                          color: (theme) => theme.palette.primary.main,
                          fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem', lg: '1.3rem', xl: '1.5rem' }
                        }}
                      >
                        {stats.enrolled.count}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4} md={3} sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'center' } }}>
                    <Paper sx={{ 
                      p: { xs: 1, sm: 1.5, md: 1.75, lg: 2, xl: 2 }, 
                      textAlign: "center", 
                      borderRadius: { xs: 1.5, sm: 1.75, md: 1.75, lg: 2, xl: 2 }, 
                      backgroundColor: (theme) => theme.palette.background.paper,
                      width: {
                        xs: '100%',
                        sm: 'min(100%, 280px)',
                        md: 'min(100%, 320px)',
                        lg: 'min(100%, 340px)',
                        xl: 'min(100%, 360px)',
                      },
                    }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '0.875rem' } }}
                      >
                        Completed
                      </Typography>
                      <Typography 
                        variant={isXs || isSm ? 'body1' : 'h6'}
                        sx={{ 
                          mt: { xs: 0.5, sm: 0.75, md: 0.875, lg: 1, xl: 1 }, 
                          fontWeight: 700, 
                          color: (theme) => theme.palette.success.main,
                          fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem', lg: '1.3rem', xl: '1.5rem' }
                        }}
                      >
                        {stats.completed.count}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4} md={3} sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'center' } }}>
                    <Paper sx={{ 
                      p: { xs: 1, sm: 1.5, md: 1.75, lg: 2, xl: 2 }, 
                      textAlign: "center", 
                      borderRadius: { xs: 1.5, sm: 1.75, md: 1.75, lg: 2, xl: 2 }, 
                      backgroundColor: (theme) => theme.palette.background.paper,
                      width: {
                        xs: '100%',
                        sm: 'min(100%, 280px)',
                        md: 'min(100%, 320px)',
                        lg: 'min(100%, 340px)',
                        xl: 'min(100%, 360px)',
                      },
                    }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '0.875rem' } }}
                      >
                        Abandoned
                      </Typography>
                      <Typography 
                        variant={isXs || isSm ? 'body1' : 'h6'}
                        sx={{ 
                          mt: { xs: 0.5, sm: 0.75, md: 0.875, lg: 1, xl: 1 }, 
                          fontWeight: 700, 
                          color: (theme) => theme.palette.error.main,
                          fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem', lg: '1.3rem', xl: '1.5rem' }
                        }}
                      >
                        {stats.abandoned.count}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
                <Box sx={{ height: { xs: 12, sm: 14, md: 15, lg: 16, xl: 16 } }} />
                <Paper sx={{ 
                  p: { xs: 1.5, sm: 2, md: 2.5, lg: 3, xl: 3 },
                  px: { xs: 1, sm: 2, md: 2.5, lg: 3, xl: 3 },
                  mt: 0, 
                  backgroundColor: (theme) => theme.palette.background.paper,
                  borderRadius: { xs: 1.5, sm: 1.75, md: 1.75, lg: 2, xl: 2 },
                }}>
                  <Typography 
                    variant={isXs || isSm ? 'subtitle1' : 'h6'}
                    gutterBottom
                    sx={{ 
                      fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem', lg: '1.125rem', xl: '1.25rem' },
                      mb: { xs: 1, sm: 1.25, md: 1.5, lg: 1.75, xl: 2 },
                    }}
                  >
                    Workflow Performance (Selected Range)
                  </Typography>
                  <WorkflowPerformanceLineChart
                    data={chartData}
                    syncId={`workflow-analytics-${workflowId}-${startDateStr}-${endDateStr}`}
                    height={chartHeight}
                    tickFontSize={tickFontSize}
                    axisTickColor={axisTickColor}
                    tooltipContentStyle={tooltipStyles.contentStyle}
                    tooltipLabelStyle={tooltipStyles.labelStyle}
                    legendFontSize={legendFontSize}
                    lineStrokeWidth={lineStrokeWidth}
                    compactChart={isXs || isSm}
                  />
                </Paper>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsTab; 